import { useEffect, useState } from "react";
import { Inbox, Images, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/store";
import { chip } from "@/components/site/OrderSpecFields";
import { TRADE_PARTS } from "@/lib/tradeCatalog";

const PART = Object.fromEntries(TRADE_PARTS);

const STATUS = [["nova", "Nova"], ["em_analise", "Em análise"], ["concluida", "Concluída"]];
const LABELS = { sobre: "Sobre", bateria: "Saúde da Bateria", pecas: "Histórico de Peças e Serviço", foto: "Foto do aparelho" };
const MARK = { nenhuma: "Nenhuma", leves: "Leves", moderadas: "Moderadas", severas: "Severas" };
const yes = (b) => (b ? "Sim" : "Não");
const when = (iso) => new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

const facts = (r) => [
  ["Bateria", r.battery != null ? `${r.battery}%` : "—"],
  ["Caixa", yes(r.has_box)],
  ["Peças trocadas", r.parts_replaced ? `${(r.parts || []).map((p) => PART[p] || p).join(", ") || "Sim"}${r.parts_detail ? ` (${r.parts_detail})` : ""}` : "Não"],
  ["Peça desconhecida", yes(r.unknown_part)],
  ["Comprado na RC", `${yes(r.bought_at_rc)}${r.estimate?.bonus > 0 ? ` (bônus ${formatPrice(r.estimate.bonus)})` : ""}`],
  ["Marcas de uso", MARK[r.marks]],
  ["Tela trincada", yes(r.screen_cracked)],
  ["Tampa trincada", yes(r.back_cracked)],
];

const wantedText = (r) => [r.wanted, ...(r.extra_wanted || [])].filter((w) => w?.label)
  .map((w) => `${w.label}${w.price > 0 ? ` · ${formatPrice(w.price)}${w.source === "pedido" ? " (média)" : ""}` : ""}`).join(" + ") || r.wanted_text || "—";

const Facts = ({ d }) => (
  <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-4">
    {facts(d).map(([k, v]) => <div key={k} className="min-w-0"><dt className="text-zinc-400">{k}</dt><dd className="truncate text-zinc-100">{v}</dd></div>)}
  </dl>
);

const Extra = ({ d, n }) => (
  <div data-testid={`trade-request-device-${n}`} className="mt-3 rounded-xl border border-white/10 p-3">
    <p className="flex flex-wrap justify-between gap-2 text-sm">
      <span className="font-semibold text-white">Aparelho {n} · {[d.model, d.storage, d.color].filter(Boolean).join(" · ")}</span>
      <span className="text-zinc-300">{d.estimate ? <>Estimado <strong className="text-white">{formatPrice(d.estimate.value)}</strong></> : "Sem valor cadastrado"}</span>
    </p>
    <Facts d={d} />
  </div>
);

const Prints = ({ id, onClose }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!id) return undefined;
    let alive = true;
    setData(null);
    api.get(`/admin/trade-requests/${id}`).then((r) => alive && setData(r?.data || null)).catch(() => alive && setData({ images: [] }));
    return () => { alive = false; };
  }, [id]);
  const images = Array.isArray(data?.images) ? data.images : [];
  const groups = [[1, images], ...(data?.extra_devices || []).map((d, i) => [i + 2, Array.isArray(d.images) ? d.images : []])].filter(([, list]) => list.length);
  const many = (data?.extra_devices || []).length > 0;
  return (
    <Dialog open={!!id} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="trade-prints-dialog" className="dark max-h-[90vh] max-w-3xl overflow-y-auto border-white/10 bg-[#111113] text-white">
        <DialogTitle className="font-display text-xl">Prints · {data?.model || "…"}</DialogTitle>
        <DialogDescription className="text-zinc-400">{data ? `${data.name} · avaliação ${data.code}` : "Carregando…"}</DialogDescription>
        {!data && <Loader2 className="mx-auto my-10 h-6 w-6 animate-spin text-zinc-400" />}
        {data && groups.length === 0 && <p className="py-6 text-sm text-zinc-400">O cliente não anexou prints.</p>}
        {groups.map(([n, list]) => (
          <div key={n}>
            {many && <p className="mb-2 mt-2 text-sm font-semibold text-white">Aparelho {n}</p>}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {list.map((img, i) => (
                <figure key={i} data-testid={`trade-print-image-${img.label}${n > 1 ? `-${n}` : ""}`} className="overflow-hidden rounded-2xl border border-white/10 bg-black">
                  <img src={img.data_url} alt={LABELS[img.label]} className="max-h-[70vh] w-full object-contain" />
                  <figcaption className="px-3 py-2 text-xs text-zinc-300">{LABELS[img.label]}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        ))}
      </DialogContent>
    </Dialog>
  );
};

const RequestCard = ({ r, onStatus, onDelete, onPrints }) => (
  <li data-testid={`trade-request-${r.id}`} className="rounded-2xl border border-white/10 bg-white/[0.03] p-4">
    <div className="flex flex-wrap items-start justify-between gap-2">
      <div className="min-w-0">
        <p className="text-xs text-zinc-400">{when(r.created_at)} · nº {r.code}</p>
        <p className="mt-0.5 font-semibold text-white">{r.name} · {[r.model, r.storage, r.color].filter(Boolean).join(" · ")}{r.extra_devices?.length ? ` + ${r.extra_devices.length} aparelho${r.extra_devices.length > 1 ? "s" : ""}` : ""}</p>
      </div>
      <p data-testid={`trade-request-estimate-${r.id}`} className="text-right text-sm text-zinc-300">{r.estimate ? <>Estimado <strong className="text-white">{formatPrice(r.estimate.value)}</strong></> : "Sem valor cadastrado"}</p>
    </div>
    <Facts d={r} />
    {(r.extra_devices || []).map((d, i) => <Extra key={i} d={d} n={i + 2} />)}
    <p data-testid={`trade-request-wanted-${r.id}`} className="mt-3 text-xs text-zinc-300">
      <span className="text-zinc-400">Quer levar:</span> {wantedText(r)}
      {r.extra_devices?.length > 0 && r.total && <> · <span className="text-zinc-400">Total das avaliações:</span> <strong className="text-white">{r.total.value > 0 ? formatPrice(r.total.value) : "a avaliar"}{r.total.value > 0 && !r.total.all_priced ? " + a avaliar" : ""}</strong></>}
      {r.total?.change > 0 && <> · <span className="text-zinc-400">Volta para o cliente:</span> <strong data-testid={`trade-request-change-${r.id}`} className="text-emerald-300">{formatPrice(r.total.change)}</strong></>}
      {r.total?.difference != null && !(r.total?.change > 0) && <> · <span className="text-zinc-400">Diferença:</span> <strong className="text-white">{r.total.difference > 0 ? formatPrice(r.total.difference) : "R$ 0,00"}</strong></>}
    </p>
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {STATUS.map(([id, label]) => (
        <button key={id} type="button" data-testid={`trade-request-status-${r.id}-${id}`} onClick={() => onStatus(r, id)} className={`${chip(r.status === id)} !px-3 !py-1.5 !text-xs`}>{label}</button>
      ))}
      <button type="button" data-testid={`trade-request-prints-${r.id}`} onClick={() => onPrints(r.id)} className="ml-auto inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-white hover:bg-white/10"><Images className="h-3.5 w-3.5" /> Ver prints ({r.image_count})</button>
      <button type="button" data-testid={`trade-request-delete-${r.id}`} onClick={() => onDelete(r)} aria-label="Excluir" className="rounded-full p-2 text-zinc-400 hover:bg-white/10 hover:text-white"><Trash2 className="h-4 w-4" /></button>
    </div>
  </li>
);

export const TradeRequests = () => {
  const [items, setItems] = useState([]);
  const [printsOf, setPrintsOf] = useState(null);
  const load = () => api.get("/admin/trade-requests").then((r) => setItems(Array.isArray(r?.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const onStatus = async (r, status) => {
    try {
      await api.patch(`/admin/trade-requests/${r.id}`, { status });
      setItems((list) => list.map((x) => (x.id === r.id ? { ...x, status } : x)));
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const onDelete = async (r) => {
    if (!window.confirm(`Excluir a avaliação de ${r.name}?`)) return;
    await api.delete(`/admin/trade-requests/${r.id}`).catch(() => null);
    setItems((list) => list.filter((x) => x.id !== r.id));
  };
  const fresh = items.filter((r) => r.status === "nova").length;

  return (
    <section data-testid="admin-trade-requests" className="mt-8 rounded-[24px] border border-white/10 bg-[#111113] p-5 sm:p-6">
      <div className="flex items-center gap-2.5">
        <Inbox className="h-5 w-5 text-white" />
        <h2 className="font-display text-xl font-bold">Avaliações de troca</h2>
        {fresh > 0 && <span data-testid="trade-requests-new-count" className="rounded-full bg-[#25D366] px-2.5 py-0.5 text-xs font-bold text-black">{fresh} nova{fresh > 1 ? "s" : ""}</span>}
      </div>
      {items.length === 0 && <p className="mt-3 text-sm text-zinc-400">Nenhuma avaliação ainda. Elas aparecem aqui quando um cliente envia pela página Troca.</p>}
      <ul className="mt-4 space-y-3">
        {items.map((r) => <RequestCard key={r.id} r={r} onStatus={onStatus} onDelete={onDelete} onPrints={setPrintsOf} />)}
      </ul>
      <Prints id={printsOf} onClose={() => setPrintsOf(null)} />
    </section>
  );
};
